import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { trackAction, trackModalOpen, trackSearch } from '../lib/utils';
import DuplicateWarningDialog from '../components/common/DuplicateWarningDialog';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Users, Plus, Edit, Trash2, MoreVertical, Loader2, Key, KeyRound, CheckSquare, Square, Archive, RotateCcw, Cloud, Download } from 'lucide-react';
import { toast } from 'sonner';

const ResponsiblesPage = () => {
  const { api, isAdmin, hasPermission, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [responsibles, setResponsibles] = useState([]);
  const [deletedResponsibles, setDeletedResponsibles] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResponsible, setEditingResponsible] = useState(null);

  // Azure AD Directory state
  const [showAzurePanel, setShowAzurePanel] = useState(false);
  const [azureUsers, setAzureUsers] = useState([]);
  const [azureLoading, setAzureLoading] = useState(false);
  const [importingIds, setImportingIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  
  // Permission check for bulk delete
  const canBulkDelete = hasPermission('bulk_delete_responsibles') || isSuperAdmin();
  
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    status: 'active',
    password: ''
  });

  useEffect(() => {
    loadResponsibles();
  }, []);

  const loadResponsibles = async () => {
    setLoading(true);
    try {
      const response = await api().get('/responsibles');
      setResponsibles(response.data);
    } catch (error) {
      toast.error('Error al cargar responsables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'deleted' && deletedResponsibles.length === 0) {
      loadDeletedResponsibles();
    }
  }, [activeTab]);

  const loadDeletedResponsibles = async () => {
    setLoadingDeleted(true);
    try {
      const response = await api().get('/responsibles/deleted');
      setDeletedResponsibles(response.data);
    } catch (error) {
      console.error('Error loading deleted responsibles:', error);
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleRestoreResponsible = async (id, name) => {
    try {
      await api().post(`/responsibles/${id}/restore`);
      toast.success(`Responsable "${name}" restaurado`);
      loadDeletedResponsibles();
      loadResponsibles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePermanentDeleteResponsible = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}" PERMANENTEMENTE?\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      await api().delete(`/responsibles/${id}/permanent`);
      toast.success(`Responsable "${name}" eliminado permanentemente`);
      loadDeletedResponsibles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEmptyResponsiblesTrash = async () => {
    if (!window.confirm(`¿VACIAR PAPELERA?\n\nEsto eliminará PERMANENTEMENTE ${deletedResponsibles.length} responsable(s).\n\nEsta acción NO se puede deshacer.`)) return;
    try {
      const response = await api().delete('/responsibles/permanent-all');
      toast.success(`Papelera vaciada: ${response.data.deleted} responsables eliminados`);
      loadDeletedResponsibles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vaciar papelera');
    }
  };

  // Bulk selection functions
  const toggleSelectItem = (itemId, e) => {
    e?.stopPropagation?.();
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === responsibles.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(responsibles.map(r => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(`¿Eliminar ${selectedItems.size} responsable(s)?`)) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = [];
    
    try {
      for (const itemId of selectedItems) {
        try {
          await api().delete(`/responsibles/${itemId}`);
          deleted++;
        } catch (error) {
          const errorMsg = error.response?.data?.detail || 'Error desconocido';
          const responsible = responsibles.find(r => r.id === itemId);
          errors.push(`${responsible?.name || itemId}: ${errorMsg}`);
          console.error(`Error deleting responsible ${itemId}:`, error);
        }
      }
      
      if (deleted > 0) {
        toast.success(`${deleted} responsable(s) eliminado(s)`);
      }
      
      if (errors.length > 0) {
        toast.error(`No se pudieron eliminar ${errors.length} responsable(s):\n${errors.slice(0, 3).join('\n')}${errors.length > 3 ? '\n...' : ''}`, {
          duration: 8000
        });
      }
      
      setSelectedItems(new Set());
      loadResponsibles();
    } catch (error) {
      toast.error('Error al eliminar responsables');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openCreateModal = () => {
    setEditingResponsible(null);
    setForm({ name: '', email: '', phone: '', department: '', status: 'active', password: '' });
    setIsModalOpen(true);
  };

  // Azure AD Directory functions
  const loadAzureDirectory = async () => {
    setAzureLoading(true);
    try {
      const res = await api().get('/auth/microsoft/directory-users');
      setAzureUsers(res.data?.users || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar directorio de Azure AD');
      setAzureUsers([]);
    } finally {
      setAzureLoading(false);
    }
  };

  const importFromAzure = async (adUser) => {
    setImportingIds(prev => new Set([...prev, adUser.azure_id]));
    try {
      const res = await api().post('/auth/microsoft/import-responsible', {
        name: adUser.name,
        email: adUser.email,
        phone: adUser.phone || ''
      });
      if (res.data?.existing) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
        loadResponsibles();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
    } finally {
      setImportingIds(prev => { const n = new Set(prev); n.delete(adUser.azure_id); return n; });
    }
  };

  const openEditModal = (responsible) => {
    setEditingResponsible(responsible);
    setForm({
      name: responsible.name,
      email: responsible.email || '',
      phone: responsible.phone || '',
      department: responsible.department || '',
      status: responsible.status,
      password: ''  // Never show existing password
    });
    setIsModalOpen(true);
  };

  const [duplicateError, setDuplicateError] = useState(null);

  const performCreateResp = async (data, forceCreate = false) => {
    const url = forceCreate ? '/responsibles?force_create=true' : '/responsibles';
    return api().post(url, data);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      const data = { ...form };
      // Only send password if it's not empty
      if (!data.password) {
        delete data.password;
      }

      if (editingResponsible) {
        await api().put(`/responsibles/${editingResponsible.id}`, data);
        toast.success('Responsable actualizado');
      } else {
        await performCreateResp(data, false);
        toast.success('Responsable creado');
      }
      setIsModalOpen(false);
      loadResponsibles();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.duplicate) {
        setDuplicateError(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = async () => {
    setDuplicateError(null);
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.password) delete data.password;
      await performCreateResp(data, true);
      toast.success('Responsable creado (duplicado forzado)');
      setIsModalOpen(false);
      loadResponsibles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Mover este responsable a la papelera?')) return;
    
    try {
      await api().delete(`/responsibles/${id}`);
      toast.success('Responsable movido a papelera');
      loadResponsibles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="responsibles-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Responsables</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los responsables de equipos y sus contraseñas
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setShowAzurePanel(true); loadAzureDirectory(); }} variant="outline" data-testid="azure-ad-btn">
            <Cloud className="h-4 w-4 mr-2" />
            Importar desde Azure AD
          </Button>
          <Button onClick={openCreateModal} className="btn-primary" data-testid="add-responsible-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Responsable
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('active')}
        >
          <Users className="h-4 w-4 mr-2" />
          Responsables Activos ({responsibles.length})
        </Button>
        {hasPermission('delete_responsibles') && (
          <Button
            variant={activeTab === 'deleted' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('deleted')}
          >
            <Archive className="h-4 w-4 mr-2" />
            Papelera ({deletedResponsibles.length})
          </Button>
        )}
      </div>

      {/* Active Tab Content */}
      {activeTab === 'active' && (
        <>
          {/* Bulk Selection Bar */}
          {canBulkDelete && responsibles.length > 0 && (
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg border">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
              >
                {selectedItems.size === responsibles.length && responsibles.length > 0 ? (
                  <CheckSquare className="h-5 w-5 text-primary" />
                ) : (
                  <Square className="h-5 w-5" />
                )}
                {selectedItems.size === responsibles.length ? 'Deseleccionar' : 'Seleccionar todos'}
              </button>
              
              {selectedItems.size > 0 && (
                <>
                  <span className="text-sm text-muted-foreground">
                    {selectedItems.size} seleccionado(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Eliminar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems(new Set())}
                  >
                    Cancelar
                  </Button>
                </>
              )}
            </div>
          )}

          <Card className="card-hover">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                {canBulkDelete && <TableHead className="w-12"></TableHead>}
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Contraseña</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={canBulkDelete ? 8 : 7}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : responsibles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canBulkDelete ? 8 : 7} className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No hay responsables</p>
                  </TableCell>
                </TableRow>
              ) : (
                responsibles.map((resp) => (
                  <TableRow key={resp.id} className="table-row" data-testid={`responsible-row-${resp.id}`}>
                    {canBulkDelete && (
                      <TableCell>
                        <button
                          onClick={(e) => toggleSelectItem(resp.id, e)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                        >
                          {selectedItems.has(resp.id) ? (
                            <CheckSquare className="h-5 w-5 text-primary" />
                          ) : (
                            <Square className="h-5 w-5 text-muted-foreground" />
                          )}
                        </button>
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{resp.name}</TableCell>
                    <TableCell>{resp.email || '-'}</TableCell>
                    <TableCell>{resp.phone || '-'}</TableCell>
                    <TableCell>{resp.department || '-'}</TableCell>
                    <TableCell>
                      {resp.has_password ? (
                        <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                          <KeyRound className="h-3 w-3 mr-1" />
                          Configurada
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                          <Key className="h-3 w-3 mr-1" />
                          Sin configurar
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={resp.status === 'active' ? 'status-disponible' : 'status-baja'}>
                        {resp.status === 'active' ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(resp)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(resp.id)} className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </>
      )}

      {/* Deleted Responsibles Tab (Trash) */}
      {activeTab === 'deleted' && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Archive className="h-5 w-5" />
                <h3 className="font-semibold">Responsables Eliminados</h3>
              </div>
              {deletedResponsibles.length > 0 && (
                <Button variant="destructive" size="sm" onClick={handleEmptyResponsiblesTrash}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Vaciar Papelera
                </Button>
              )}
            </div>
            {loadingDeleted ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : deletedResponsibles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Archive className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay responsables eliminados</p>
              </div>
            ) : (
              <div className="space-y-2">
                {deletedResponsibles.map(resp => (
                  <div key={resp.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{resp.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {resp.email || 'Sin email'} - Eliminado: {resp.deleted_at ? new Date(resp.deleted_at).toLocaleDateString() : 'Fecha desconocida'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRestoreResponsible(resp.id, resp.name)}
                        className="text-green-500 hover:text-green-400"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restaurar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handlePermanentDeleteResponsible(resp.id, resp.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingResponsible ? 'Editar Responsable' : 'Nuevo Responsable'}</DialogTitle>
            <DialogDescription>
              {editingResponsible ? 'Modifica los datos del responsable' : 'Registra un nuevo responsable'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre completo"
                data-testid="responsible-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  data-testid="responsible-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  data-testid="responsible-phone-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="department">Departamento</Label>
                <Input
                  id="department"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  placeholder="Departamento"
                  data-testid="responsible-department-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Estado</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger data-testid="responsible-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Password section */}
            <div className="space-y-2 pt-2 border-t border-border/40">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Key className="h-4 w-4 text-primary" />
                Contraseña para Movimientos
              </Label>
              <PasswordInput
                id="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingResponsible?.has_password ? "Dejar vacío para mantener" : "Establecer contraseña"}
                data-testid="responsible-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Esta contraseña se usará para verificar la identidad al crear movimientos de equipo
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-responsible-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingResponsible ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Azure AD Directory Panel */}
      <Dialog open={showAzurePanel} onOpenChange={setShowAzurePanel}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              Directorio Azure AD
            </DialogTitle>
            <DialogDescription>
              Importa usuarios de Microsoft Azure AD como responsables
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {azureLoading ? (
              <div className="space-y-2 p-4">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : azureUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Cloud className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No se encontraron usuarios en el directorio</p>
                <p className="text-xs mt-1">Verifica que la app tenga permiso User.Read.All en Azure AD</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Puesto</TableHead>
                    <TableHead>Depto</TableHead>
                    <TableHead className="text-right">Accion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {azureUsers.map(u => {
                    const alreadyExists = responsibles.some(r => r.name === u.name || r.email === u.email);
                    return (
                      <TableRow key={u.azure_id}>
                        <TableCell className="font-medium text-sm">{u.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                        <TableCell className="text-xs">{u.job_title || '-'}</TableCell>
                        <TableCell className="text-xs">{u.department || '-'}</TableCell>
                        <TableCell className="text-right">
                          {alreadyExists ? (
                            <Badge variant="outline" className="text-xs">Ya existe</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => importFromAzure(u)}
                              disabled={importingIds.has(u.azure_id)}
                              data-testid={`import-ad-${u.azure_id}`}
                            >
                              {importingIds.has(u.azure_id) ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <><Download className="h-3 w-3 mr-1" />Importar</>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DuplicateWarningDialog
        duplicateError={duplicateError}
        onCancel={() => setDuplicateError(null)}
        onForceCreate={handleForceCreate}
        entityLabel="responsable"
      />
    </div>
  );
};

export default ResponsiblesPage;
